import { Button, Modal, Table } from 'antd';
import { TablePaginationConfig } from 'antd/lib/table';
import config from 'config';
import {
  DonationListSort,
  Order,
  useGetSpecificDonorDonationsListQuery,
} from 'gql/types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { columns } from './columns';

import { IDonationTableRow, IDonorTableRow, TLocationItem } from '../../types';

interface IProps {
  visible: boolean;
  donor: IDonorTableRow | null;
  allLocations: TLocationItem[];
  onClose: () => void;
}

const DonationsModal: React.FC<IProps> = ({
  visible,
  donor,
  allLocations,
  onClose,
}) => {
  const { t } = useTranslation('dashboard.DonorDashboard.DonationsModal');
  const [current, setCurrent] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = config.PAGINATION.PAGE_SIZE;
  const offset = (current - 1) * pageSize;

  useEffect(() => {
    setCurrent(1);
  }, [donor?.id]);

  const paginationConfig = useMemo(
    (): TablePaginationConfig => ({
      current,
      pageSize,
      total,
      onChange: (page) => setCurrent(page),
      showSizeChanger: false,
      hideOnSinglePage: true,
    }),
    [current, pageSize, total]
  );

  const { data, loading } = useGetSpecificDonorDonationsListQuery({
    variables: {
      input: {
        filter: { donorId: donor?.id ?? '' },
        skip: offset,
        take: pageSize,
        sort: [
          { fieldName: DonationListSort.LastDonationDate, order: Order.Desc },
        ],
      },
    },
    skip: !donor,
    onCompleted: (result) => {
      setTotal(result.getSpecificDonorDonationsList.meta.total);
    },
  });

  const donations = useMemo((): IDonationTableRow[] => {
    const items = data?.getSpecificDonorDonationsList?.data ?? [];
    return items.map((d) => {
      const loc = allLocations.find((l) => l.name === d.donor.location);
      return {
        key: d.id,
        id: d.id,
        date: d.createdAt,
        location: d.donor.location,
        region: loc?.region?.name ?? t('unknown'),
        bloodType: d.donor?.bloodType,
        collectionSet: d.collectionSet,
        capillaryRefillTime: d.capillaryRefillTime,
        totalCollectedMls: d.additionalInfo?.totalCollectedMls,
        donationMLApprovedAmount: d.approvedDonationsMl,
        veterinarian: d.donor.primaryVeterinarian,
        rawDonation: d,
      };
    });
  }, [data, allLocations]);

  return (
    <Modal
      title={donor ? t('title', { donorName: donor.name }) : t('defaultTitle')}
      visible={visible}
      onCancel={onClose}
      width={1200}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('close')}
        </Button>,
      ]}
    >
      <Table
        dataSource={donations}
        columns={columns}
        loading={loading}
        pagination={{ ...paginationConfig, showQuickJumper: true }}
        size="small"
        scroll={{ x: 1000 }}
      />
    </Modal>
  );
};

export default DonationsModal;
